import { Button, useIsMobile } from '@bpinternal/ui-kit-next'
import { useQuery as useTanstackQuery } from '@tanstack/react-query'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import { ChevronRight, ChevronsUpDown, Home } from 'lucide-react'
import { match } from 'ts-pattern'
import { type BreadcrumbPage, Breadcrumbs } from '~/components'
import { useCurrentRouteId } from '~/hooks'
import { useQuery } from '~/services'
import { cn, titleCase, truncateText } from '~/utils'
import { listBotsQueryOptions } from '../queries'
import { BotsDropdown } from './navigation/BotsDropdown'
import { TableSelectorMenu } from './TableSelectorMenu'

type Props = {
  workspaceId?: string
  botId?: string
  tableId?: string
  kbId?: string
  className?: string
}

const ignoredRoutes = [
  'workspaces',
  '$integrationId',
  'bots',
  'v1',
  'v2',
  'new-bot',
  'new-chat',
  'create-bot',
  '$dsId',
] as const

export const NavbarBreadcrumbs = ({ botId, workspaceId, tableId, kbId, className }: Props) => {
  const routeId = useCurrentRouteId() ?? ''
  const params = useParams({ strict: false })
  const search = useSearch({ strict: false })
  const isMobile = useIsMobile()

  const { data: bots } = useTanstackQuery(listBotsQueryOptions(workspaceId ?? ''))
  const { data: tables } = useQuery('workspaces_/$workspaceId_/bots_/$botId_/tables/$tags/all', {
    botId: botId ?? '',
    workspaceId: workspaceId ?? '',
    tags: {},
  })

  const { data: bot } = useQuery('workspaces_/$workspaceId_/bots_/$botId_', {
    botId: botId ?? '',
    workspaceId: workspaceId ?? '',
  })

  const { data: workspace } = useQuery('workspaces_/$workspaceId_', {
    workspaceId: workspaceId ?? '',
  })

  const { data: table } = useQuery('workspaces_/$workspaceId_/bots_/$botId_/tables/$table_', {
    botId: botId ?? '',
    workspaceId: workspaceId ?? '',
    table: tableId ?? '',
  })

  const { data: kb } = useQuery('workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_/$kbId_', {
    botId: botId ?? '',
    workspaceId: workspaceId ?? '',
    kbId: kbId ?? '',
  })

  const routes = routeId.split('/').filter(Boolean)
  const isOnBotsPage = routes.includes('bots') && botId

  if (isMobile) {
    const botName = bot?.name ?? params.botId ?? 'Bot'
    const truncated = truncateText(botName, 8)
    return (
      <div className={cn(className, 'flex items-center')}>
        <Link to="/workspaces/$workspaceId" params={{ workspaceId: workspaceId ?? '' }}>
          <Home className="h-4 w-4 text-muted-foreground"></Home>
        </Link>
        {isOnBotsPage && (
          <>
            <ChevronRight className="mx-1 h-4 w-4 text-muted-foreground"></ChevronRight>
            {bots ? (
              <BotsDropdown bots={bots}>
                <Button variant="ghost" className="h-6 max-w-[40vw] gap-1 px-0" title={truncated.full}>
                  <p className="truncate text-sm text-muted-foreground sm:text-sm">{truncated.display}</p>
                  <ChevronsUpDown className="h-3 w-3 text-muted-foreground sm:h-4 sm:w-4" />
                </Button>
              </BotsDropdown>
            ) : (
              <p className="truncate text-xs text-muted-foreground sm:text-sm" title={truncated.full}>
                {truncated.display}
              </p>
            )}
          </>
        )}
      </div>
    )
  }

  const breadcrumbsPages: BreadcrumbPage[] = []

  for (let i = 0; i < routes.length; i++) {
    const to = `/${routes.slice(0, i + 1).join('/')}`
    const props = { params, search, to: i !== routes.length - 1 ? to : undefined } as any
    if (to) {
      match(routes[i])
        .when(
          (route) => ignoredRoutes.includes(route ?? ''),
          () => {}
        )
        .with('$workspaceId', () => {
          const workspaceName = workspace?.name ?? 'Workspace'
          const truncated = isMobile ? truncateText(workspaceName, 12) : { display: workspaceName, full: workspaceName }
          breadcrumbsPages.push({
            ...props,
            name: truncated.display,
          })
        })
        .with('$botId', () => {
          const botName = bot?.name ?? params.botId ?? 'Bot'
          const truncated = isMobile ? truncateText(botName, 8) : { display: botName, full: botName }
          breadcrumbsPages.push({
            ...props,
            name: '',
            trailing: bots ? (
              <BotsDropdown bots={bots}>
                <Button
                  variant="ghost"
                  className="-ml-2 flex h-6 min-w-0 flex-row gap-1 px-1 sm:gap-2 sm:px-2"
                  title={truncated.full}
                >
                  <span className="min-w-0 flex-1 truncate text-xs font-normal text-muted-foreground sm:text-sm">
                    {truncated.display}
                  </span>
                  <ChevronsUpDown className="h-3 w-3 flex-shrink-0 text-muted-foreground sm:h-4 sm:w-4" />
                </Button>
              </BotsDropdown>
            ) : undefined,
          })
        })
        .with('$kbId', () => {
          breadcrumbsPages.push({
            ...props,
            name: kb?.name ?? 'Knowledge Base',
          })
        })
        .with('$tableId', () => {
          breadcrumbsPages.push({
            ...props,
            name: table?.table.name ?? 'Table',
            trailing: tables ? (
              <TableSelectorMenu tables={tables.tables}>
                <Button variant="ghost" size="icon" className="-mr-2 h-6 w-6">
                  <ChevronsUpDown className="text-muted-foreground" />
                </Button>
              </TableSelectorMenu>
            ) : undefined,
          })
        })
        .otherwise(() => {
          breadcrumbsPages.push({
            ...props,
            name: titleCase(routes[i] ?? ''),
          })
        })
    }
  }

  return (
    <Breadcrumbs pages={breadcrumbsPages} className={cn('shrink-1 min-w-0 max-w-full gap-1 md:gap-2', className)} />
  )
}
